import React, { useEffect, useState } from 'react'
import { NavigateFunction } from 'react-router'
import { logger } from 'app/components/logger'
import { useReport } from 'app/modules/reports/hooks'
import { getPageUrl } from '../routing'
import type { ITranslator } from '../hooks/use-translation'
import { searchDateToObj } from 'app/modules/reports/features/search/threat-hunting'

export function emphasizeUrls(text: string, urlText: string | null = null): string {
  // Match any existing URL in a string
  const urlMatcher =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{2,32}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/
  const match = text.match(urlMatcher)

  if (match) {
    const url = match[0]
    const element = `<a href="${url}" target="_blank">${urlText ?? url}</a>`
    return text.replace(url, element)
  }

  return text
}

export function decimalToHexString(numb: number) {
  if (numb < 0) {
    numb = 0xffffffff + numb + 1
  }
  return '0x' + numb.toString(16).toUpperCase()
}

//Sets or clears given http query param and reloads page
export function updateSearchQuery(
  url: string,
  query: URLSearchParams,
  navigate: NavigateFunction,
  name: string | { [key: string]: string | undefined },
  value?: string | undefined
) {
  function setQueryParam(name: string, value: string | undefined) {
    value === undefined || value === '' ? query.delete(name) : query.set(name, value)
  }

  if (typeof name === 'string') {
    setQueryParam(name, value)
  } else {
    Object.keys(name).forEach((key) => {
      setQueryParam(key, name[key])
    })
  }

  const queryStr = query.toString()

  navigate(queryStr ? `${url}?${queryStr}` : url)
}

// Check if string is sha256
export function isSha256(str: string, strict = false) {
  return typeof str === 'string' && (strict ? !!str.match(/^[a-z0-9]{64}$/gi) : str.length === 64 && !str.includes(' '))
}

export function countItems(data: any[] | { [key: string]: any }, limit?: number) {
  let count = 0

  if (Array.isArray(data)) {
    count += data.length

    for (const item of data) {
      count += countItems(item)

      if (limit && count >= limit) {
        return count
      }
    }
  } else if (data !== null && typeof data === 'object') {
    for (const key of Object.keys(data)) {
      count += countItems(data[key]) + 1 // + 1 - for current element that we check

      if (limit && count >= limit) {
        return count
      }
    }
  }

  return count
}

// Check if object or array has too much elements to be used in FE json viewer.
// Too much here just means that it would hit performance.
export function isJsonViewTooMuchNested(data: any, limit?: number) {
  const count = countItems(data, limit)
  return count > 1000
}

// Check if we have text-like content
export function isAsciiData(data: string | undefined) {
  return !/[^\u0000-\u007f]+/.test(data || '')
}

// Escape string to use it in RegExp constructor
export function escapeRegex(str: string) {
  return str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
}

// Cut string to needed max length with `...`
export function ellipsis(str: string | undefined, limit: number) {
  return str && str.length >= limit ? str.substring(0, limit) + '...' : str
}

// Format difference between two timestamps
export function formatTimestampDiff(time: number, minutesWithSeconds = false) {
  if (time < 1000) {
    return time.toFixed(0) + ' ms'
  }

  const sec = time / 1000
  if (sec < 60) {
    const toFixed = minutesWithSeconds ? 0 : 2
    return sec.toFixed(toFixed) + ' sec'
  }

  if (!minutesWithSeconds) {
    return (sec / 60).toFixed(2) + ' min'
  }

  const minutes = (sec / 60).toFixed(0)
  const seconds = (sec % 60).toFixed(0)

  return `${minutes} min` + (seconds === '0' ? '' : ` ${seconds} sec`)
}

// Get value from object by dotted path
export function getByPath(obj: any, path: string) {
  const parts = path.split('.')
  let result = obj

  for (const key of parts) {
    if (typeof result[key] === 'undefined') {
      return undefined
    }

    result = result[key]
  }

  return result
}

// Launch browser file download
export function downloadData(data: string, fileName: string, mimeType?: string) {
  const link = globalThis.URL.createObjectURL(
    new File([data], fileName, { type: mimeType || 'application/octet-stream' })
  )

  const elem = document.createElement('a')
  elem.href = link
  elem.setAttribute('download', fileName)
  document.body.appendChild(elem)
  elem.click()

  setTimeout(() => {
    globalThis.URL.revokeObjectURL(link)
    elem.remove()
  }, 3000)
}

// Get month name by it's number with preceiding zero
export function getMonthName(number: string | number) {
  const names = [null, 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  number = Number.parseInt(number as string)

  return names[number]
}

export function mbToGb(value: number) {
  return Math.round((value / 1000) * 100) / 100 // Round to 2 decimal digits
}

// Check if dom element is visible in viewport
export function useIsDomNodeVisible(ref: any) {
  const [isIntersecting, setIntersecting] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting))

    observer.observe(ref.current)
    return () => {
      observer.disconnect()
    }
  }, [ref])

  return isIntersecting
}

export function usePageScroll(active: boolean) {
  useEffect(() => {
    document.documentElement.style.overflow = active ? '' : 'hidden'
    return () => {
      document.documentElement.style.overflow = ''
    }
  }, [active])
}

// Convert camel cased string into separate words.
// Correctly handle abbreviations (keep as  a single upper case words)
export function camelCaseToWords(str: string) {
  return str ? str.replace(/(?<![A-Z])([A-Z])/g, ' $1').replace(/([A-Z])(?![A-Z])(?=.)/g, ' $1') : ''
}

// Safely increment number
export function inc(value: number | undefined, incValue: number = 1) {
  return typeof value !== 'undefined' ? value + incValue : incValue
}

// Calculate sum of values in array or plain object
export function sum(iterable: number[] | { [key: string]: number } | undefined) {
  if (!iterable) {
    return 0
  }

  if (!Array.isArray(iterable)) {
    iterable = Object.values(iterable)
  }

  return iterable.reduce((a, b) => a + b, 0)
}

// Use array of strings as keys to create new Object with boolean `true` value
export function listToObj(list: string[] | undefined) {
  const result: { [key: string]: boolean } = {}
  for (const item of list || []) {
    result[item] = true
  }

  return result
}

// Just a shorthand to check if at least one of keys exists in object
export function objectHasOneOfKeys(obj: { [key: string]: any }, keys: string[]) {
  for (const key of keys) {
    if (typeof obj[key] !== 'undefined') {
      return true
    }
  }

  return false
}

// Replace new lines with spaces in string
export function noNewLines(content: string | undefined) {
  return content ? content.replace(/\n+\s*/g, ' ') : undefined
}

// Convert new lines to breaks in text
export function nlToBr(content: string) {
  const parts = content.split('\n')
  const result: (string | JSX.Element)[] = []

  if (parts.length === 1) {
    return content
  }

  parts.forEach((part) => {
    result.push(part, <br />)
  })

  return result
}

// Convert date obtained from BE to a Date object
export function dateObj(dateTime: string) {
  // The date is returned from the backend without timezone information and React thinks it is in local timezone
  // Need to add a Z at the end of the ISO datetime format to make it correctly parsed as UTC
  return new Date(dateTime.replace(/.{3}$/, 'Z'))
}

// Format a count with its unit, e.g. "1 minute ago" / "5 minutes ago", or the short "5 m" when `short`
function agoLabel(_t: ITranslator['_t'], value: number, unit: string, short: boolean): string {
  if (short) {
    return _t(`time-ago-${unit}-short`, { count: value })
  }
  const key = value === 1 ? `time-ago-${unit}` : `time-ago-${unit}s`
  return _t(key, { count: value })
}

// Describe how far in the past a date is, in approximate human words (e.g. "5 minutes ago", "yesterday").
// Pass `short` for an abbreviated form (e.g. "5 m", "2 y") used where space is tight.
export function timeAgo(date: Date | string, _t: ITranslator['_t'], short = false): string {
  const past = typeof date === 'string' ? searchDateToObj(date) : date
  if (!past || isNaN(past.getTime())) {
    return ''
  }

  const seconds = Math.floor((Date.now() - past.getTime()) / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) {
    return _t(short ? 'time-just-now-short' : 'time-just-now')
  }
  if (minutes < 60) {
    return agoLabel(_t, minutes, 'minute', short)
  }
  if (hours < 24) {
    return agoLabel(_t, hours, 'hour', short)
  }
  if (days === 1) {
    return _t(short ? 'time-yesterday-short' : 'time-yesterday')
  }
  if (days < 7) {
    return agoLabel(_t, days, 'day', short)
  }
  if (days < 30) {
    return agoLabel(_t, Math.floor(days / 7), 'week', short)
  }
  if (days < 365) {
    return agoLabel(_t, Math.floor(days / 30), 'month', short)
  }
  return agoLabel(_t, Math.floor(days / 365), 'year', short)
}

// Convert datetime to another format and timezone
export function convertDateTime(locale: string | undefined, timezone: string | undefined, date: any) {
  try {
    if (!date) {
      return ''
    }
    locale = normalizeLocale(locale)

    const timeZone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    const convertedDate = dateObj(date)
      .toLocaleString(locale, { timeZoneName: 'shortOffset', timeZone: timeZone })
      .replace('GMT', 'UTC')

    return convertedDate
  } catch (error) {
    logger.error(error)

    return date
  }
}

// Convert date to another format
// Returns the formatted date without the time
export function formatDate(locale: string | undefined, date: any) {
  try {
    locale = normalizeLocale(locale)
    const dateParts = new Date(date).toLocaleString(locale).split(' ')

    if (dateParts.length == 2) {
      return dateParts[0].replace(',', '')
    }

    const assembaledDate = `${dateParts[0]} ${dateParts[1]} ${dateParts[2]}`

    return assembaledDate.replace(',', '')
  } catch (error) {
    logger.error(error)

    return date
  }
}

// Detect if we are in report export mode
// Can be used everywhere on report page, unlike useExport(), that is
// limited by export context
export function useDetectExport() {
  const pageParams = useReport()
  const { subpage } = pageParams

  return subpage === 'htmlview'
}

// Add dots/commas separators to number depending on locale
export function humanReadableNumber(value: number | undefined, locale?: string) {
  if (typeof value === 'undefined' || value === null) {
    return value
  }

  locale = normalizeLocale(locale)
  return new Intl.NumberFormat(locale).format(value)
}

// Make sure locale is always set and is a valid BCP 47 language tag
export function normalizeLocale(locale?: string) {
  const candidate = locale ?? (navigator.languages[1] ? navigator.languages[1] : 'en')
  try {
    new Intl.Locale(candidate)
    return candidate
  } catch {
    return 'en'
  }
}

type ScrollPosition = 'start' | 'center' | 'end' | 'nearest'
// Scroll to element
export function scrollTo(ref: any, position: ScrollPosition = 'center', callback?: CallableFunction) {
  return setTimeout(() => {
    if (!ref.current) {
      return
    }

    ref.current?.scrollIntoView({ behavior: 'smooth', block: position })

    callback?.()
  }, 200)
}

// Scroll to element on page and highlight it for a short time
export function scrollToAndHighlight(ref: any, position: ScrollPosition = 'center') {
  return scrollTo(ref, position, () => {
    setTimeout(() => {
      if (ref.current) {
        ref.current.style.backgroundColor = '#fed975'
        ref.current.style.transition = 'background-color 0.5s ease-in-out'
      }
    }, 250)

    setTimeout(() => {
      if (ref.current) {
        ref.current.style.backgroundColor = ''
      }
    }, 1500)
  })
}

// Check if URL pathname matches pathname template, specified in routing config for some page.
// Path template means a string like /foo/bar/:param/baz, so with React Router parameters
export function checkUrlPathMatchesPagePath(urlPath: string, pageKey: string) {
  const tmplPath = getPageUrl(pageKey)
  if (typeof tmplPath === 'undefined') {
    return false
  }

  const partsUrl = urlPath.split('/')
  const partsTmpl = tmplPath.split('/')

  if (!partsUrl.length || partsUrl.length !== partsTmpl.length) {
    return false
  }

  for (let i = 0; i < partsUrl.length; i++) {
    if (partsTmpl[i].startsWith(':')) {
      continue
    }

    if (partsTmpl[i] !== partsUrl[i]) {
      return false
    }
  }

  return true
}

// Determine if application is executed inside iframe
export function isInIframe() {
  return typeof globalThis.window !== 'undefined' && globalThis.self !== globalThis.top
}

// Get difference in days between two dates
export function getDaysDiff(dateStart: Date, dateEnd: Date) {
  const dayMs = 1000 * 60 * 60 * 24
  const diff = dateEnd.getTime() - dateStart.getTime()
  const days = Math.floor(diff / dayMs)

  return days
}

// Substract some amount of days from given date
export function subtractDays(date: Date, days: number) {
  const copy = new Date(date)
  copy.setDate(copy.getDate() - days)

  return copy
}
